import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Generic autocomplete component for text input + async dropdown suggestions.
 *
 * Data attributes:
 * - data-endpoint: API URL
 * - data-query-param: query key (default: "name")
 * - data-program-id: optional program id to send as `program_id`
 * - data-tags: optional tags string to send as `tags`
 * - data-min-query-length: minimum chars before request (default: 2)
 * - data-debounce-ms: debounce delay in ms (default: 250)
 */
class AutocompleteComponent extends Component {
  /** @type {string[]} */
  requiredRefs = ['input', 'list', 'clearButton'];

  /** @type {Array<{ id: string, label: string, value: string, raw: object | string }>} */
  #items = [];
  #debouncedSearch = null;
  #requestId = 0;
  #highlightedIndex = -1;
  #suppressInputSearch = false;
  #selectedItem = null;
  #closeListTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    this.#debouncedSearch = debounce((query) => this.#search(query), this.#debounceMs);
    this.#syncClearButton();
    this.addEventListener('focusout', this.#handleFocusOut);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#debouncedSearch?.cancel?.();
    this.#clearPendingClose();
    this.removeEventListener('focusout', this.#handleFocusOut);
  }

  /**
   * Close the list when focus leaves the component entirely (tab-away,
   * click outside). Mirrors native select behavior.
   *
   * @param {FocusEvent} event
   * @returns {void}
   */
  #handleFocusOut = (event) => {
    const next = /** @type {Node | null} */ (event.relatedTarget);
    if (next && this.contains(next)) {
      this.#clearPendingClose();
      return;
    }

    this.#clearPendingClose();
    this.#closeListTimeout = setTimeout(() => {
      this.#closeListTimeout = null;

      const activeElement = document.activeElement;
      if (activeElement && this.contains(activeElement)) return;

      this.#hideList();
    }, 150);
  };

  /**
   * Handles input and triggers debounced search.
   *
   * @param {Event} event
   * @returns {void}
   */
  handleInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target !== this.refs.input) return;

    if (this.#suppressInputSearch) {
      this.#suppressInputSearch = false;
      this.#hideList();
      this.#syncClearButton();
      return;
    }

    this.#selectedItem = null;
    this.#syncClearButton();
    this.#highlightedIndex = -1;
    this.dispatchEvent(
      new CustomEvent('autocomplete:clear', {
        bubbles: true,
      }),
    );

    const query = this.refs.input.value.trim();
    if (query.length < this.#minQueryLength) {
      this.#debouncedSearch?.cancel?.();
      this.#clearList();
      return;
    }

    if (this.#hasExactMatch(query)) {
      this.#debouncedSearch?.cancel?.();
      this.#hideList();
      return;
    }

    this.#debouncedSearch?.(query);
  };

  /**
   * Handles focus on the input.
   *
   * @returns {void}
   */
  handleFocus = () => {
    this.#clearPendingClose();

    if (this.#items.length) {
      this.#showList();
    }
  };

  /**
   * Keyboard interactions for dropdown list.
   *
   * @param {KeyboardEvent} event
   * @returns {void}
   */
  handleKeydown = (event) => {
    if (!this.#items.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#highlightedIndex = Math.min(this.#highlightedIndex + 1, this.#items.length - 1);
      this.#renderList();
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.#highlightedIndex = Math.max(this.#highlightedIndex - 1, 0);
      this.#renderList();
      return;
    }

    if (event.key === 'Enter' && this.#highlightedIndex >= 0) {
      event.preventDefault();
      this.#selectByIndex(this.#highlightedIndex);
      return;
    }

    if (event.key === 'Escape') {
      this.#hideList();
    }
  };

  /**
   * Selects an item from dropdown by index.
   *
   * @param {{ index?: string | number }} data
   * @param {Event} event
   * @returns {void}
   */
  select = (data, event) => {
    event.preventDefault();
    const index = Number(data?.index);
    if (!Number.isFinite(index)) return;
    this.#selectByIndex(index);
  };

  /**
   * Clears selected/typed value.
   *
   * @param {Event} [event]
   * @returns {void}
   */
  clearSelection = (event) => {
    event?.preventDefault();
    this.#selectedItem = null;
    this.refs.input.value = '';
    this.refs.input.dispatchEvent(new Event('input', { bubbles: true }));
    this.refs.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.dispatchEvent(
      new CustomEvent('autocomplete:clear', {
        bubbles: true,
      }),
    );
    this.#clearList();
    this.#syncClearButton();
    this.refs.input.focus();
  };

  /**
   * Programmatic API to update SheerID program id and tag filter.
   *
   * @param {{ programId?: string, tags?: string }} data
   * @returns {void}
   */
  setProgram = (data = {}) => {
    this.dataset.programId = String(data.programId || '');
    this.dataset.tags = String(data.tags || '');
    if (!this.refs?.input || !this.refs?.list || !this.refs?.clearButton) return;

    this.#selectedItem = null;
    this.refs.input.value = '';
    this.dispatchEvent(
      new CustomEvent('autocomplete:clear', {
        bubbles: true,
      }),
    );
    this.#debouncedSearch?.cancel?.();
    this.#clearList();
    this.#syncClearButton();
  };

  /**
   * @param {number} index
   * @returns {void}
   */
  #selectByIndex(index) {
    const item = this.#items[index];
    if (!item) return;

    this.#clearPendingClose();
    this.#selectedItem = item;
    this.#suppressInputSearch = true;
    this.refs.input.value = item.value;
    this.refs.input.dispatchEvent(new Event('input', { bubbles: true }));
    this.refs.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.dispatchEvent(
      new CustomEvent('autocomplete:select', {
        bubbles: true,
        detail: item,
      }),
    );
    this.#hideList();
    this.#syncClearButton();
  }

  /**
   * Public API for parent components.
   *
   * @returns {boolean}
   */
  hasSelection = () => Boolean(this.#selectedItem);

  /**
   * Public API for parent components.
   *
   * @returns {{ id?: string, name?: string, label?: string, value?: string, raw?: object | string } | null}
   */
  getSelection = () => this.#selectedItem;

  /**
   * @param {string} query
   * @returns {Promise<void>}
   */
  async #search(query) {
    const endpoint = this.dataset.endpoint || '';
    if (!endpoint) {
      this.#clearList();
      return;
    }

    const requestId = ++this.#requestId;

    try {
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set(this.#queryParam, query);

      const programId = this.dataset.programId || '';
      if (programId) {
        url.searchParams.set('program_id', programId);
      }

      const tags = this.dataset.tags || '';
      if (tags) {
        url.searchParams.set('tags', tags);
      }

      const response = await fetch(url.toString(), {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const payload = await response.json();
      if (requestId !== this.#requestId) return;

      const items = this.#normalizeItems(payload);
      if (this.#hasExactMatch(query, items)) {
        this.#clearList();
        return;
      }

      this.#items = items.slice(0, 8);
      this.#highlightedIndex = -1;
      this.#renderList();
    } catch (error) {
      if (requestId !== this.#requestId) return;
      console.error('autocomplete-component: search failed', error);
      this.#clearList();
    }
  }

  /**
   * @param {unknown} payload
   * @returns {Array<{ id: string, label: string, value: string, raw: object | string }>}
   */
  #normalizeItems(payload) {
    const rawItems = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];

    return rawItems
      .map((item) => {
        if (typeof item === 'string') {
          const value = item.trim();
          if (!value) return null;
          return { id: value, label: value, value, raw: item };
        }

        if (item && typeof item === 'object') {
          const name = String(item.name || item.label || '').trim();
          if (!name) return null;
          const id = String(item.id || name);
          return { id, label: name, value: name, raw: item };
        }

        return null;
      })
      .filter(Boolean);
  }

  /**
   * @param {string} query
   * @param {Array<{ label: string, value: string }>} [items]
   * @returns {boolean}
   */
  #hasExactMatch(query, items = this.#items) {
    const normalizedQuery = String(query || '').trim().toLowerCase();
    if (!normalizedQuery) return false;

    return items.some((item) => {
      const label = String(item?.label || '').trim().toLowerCase();
      const value = String(item?.value || '').trim().toLowerCase();
      return normalizedQuery === label || normalizedQuery === value;
    });
  }

  /**
   * @returns {void}
   */
  #renderList() {
    if (!this.#items.length) {
      this.#clearList();
      return;
    }

    const list = document.createElement('ul');
    list.className = 'dropdown__menu autocomplete-field__menu';
    // The ref=list element is the listbox; the ul/li are only visual wrappers.
    list.setAttribute('role', 'presentation');

    const listboxId = this.refs.list.id;
    let activeOptionId = '';

    for (let index = 0; index < this.#items.length; index += 1) {
      const item = this.#items[index];
      const listItem = document.createElement('li');
      listItem.className = 'dropdown__item';
      listItem.setAttribute('role', 'presentation');

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'dropdown__button autocomplete-field__option';
      const isActive = index === this.#highlightedIndex;
      if (isActive) {
        button.classList.add('_active');
      }
      button.textContent = item.label;
      button.setAttribute('on:click', `autocomplete-component/select?index=${index}`);
      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      if (listboxId) {
        button.id = `${listboxId}-option-${index}`;
        if (isActive) activeOptionId = button.id;
      }
      listItem.appendChild(button);
      list.appendChild(listItem);
    }

    this.refs.list.replaceChildren(list);
    if (activeOptionId) {
      this.refs.input.setAttribute('aria-activedescendant', activeOptionId);
    } else {
      this.refs.input.removeAttribute('aria-activedescendant');
    }
    this.#showList();
  }

  /**
   * @returns {void}
   */
  #clearList() {
    this.#items = [];
    this.#highlightedIndex = -1;
    this.refs.list.replaceChildren();
    this.#hideList();
  }

  /**
   * @returns {void}
   */
  #showList() {
    this.#clearPendingClose();
    this.refs.list.classList.remove('_hidden');
    this.refs.input.setAttribute('aria-expanded', 'true');
  }

  /**
   * @returns {void}
   */
  #hideList() {
    this.refs.list.classList.add('_hidden');
    this.refs.input.setAttribute('aria-expanded', 'false');
    this.refs.input.removeAttribute('aria-activedescendant');
  }

  /**
   * @returns {void}
   */
  #clearPendingClose() {
    if (!this.#closeListTimeout) return;

    clearTimeout(this.#closeListTimeout);
    this.#closeListTimeout = null;
  }

  /**
   * @returns {void}
   */
  #syncClearButton() {
    const hasValue = Boolean(this.refs.input.value?.trim());
    this.refs.clearButton.classList.toggle('_hidden', !hasValue);
  }

  /**
   * @returns {number}
   */
  get #minQueryLength() {
    const value = Number(this.dataset.minQueryLength || 2);
    return Number.isFinite(value) && value > 0 ? value : 2;
  }

  /**
   * @returns {number}
   */
  get #debounceMs() {
    const value = Number(this.dataset.debounceMs || 250);
    return Number.isFinite(value) && value >= 0 ? value : 250;
  }

  /**
   * @returns {string}
   */
  get #queryParam() {
    return this.dataset.queryParam || 'name';
  }
}

if (!customElements.get('autocomplete-component')) {
  customElements.define('autocomplete-component', AutocompleteComponent);
}
